import type { OrderItemIn, OrderUpdatePayload } from '../../api/serviceOrders';
import type { Draft, DraftLine, ItemType } from './types';

// Сборка payload для POST/PUT /api/service-orders — общая для обоих видов.
// Схема: ServiceOrderPage.md §2В (rev. 2026-09-08).

export const lineToPayload = (l: DraftLine, type: ItemType): OrderItemIn => ({
  item_type: type,
  name: l.name,
  external_1c_id: l.external_1c_id,
  quantity: l.quantity,
  unit: l.unit || 'шт',
  price: l.price,
  sum: l.sum,
  executor1_id: l.executor1_id ?? null,
  executor2_id: l.executor2_id ?? null,
  executor3_id: l.executor3_id ?? null,
  vat: l.vat,
  vat_rate: l.vat_rate,
  sum_with_vat: l.sum_with_vat,
  norm_hour: l.norm_hour,
  work_code: l.work_code,
  marking_code: l.marking_code ?? null,
});

export const buildPayload = (draft: Draft): OrderUpdatePayload => {
  const servicesPayload = draft.services.filter(l => l.name.trim()).map(l => lineToPayload(l, 'service'));
  const partsPayload = draft.parts.filter(l => l.name.trim()).flatMap(l => {
    if (l.is_bulk_master || l.is_piece_master) {
      // Мастер-строка разворачивается в N позиций — 1 марка = 1 строка
      // (разлив: rev. 2026-08-10, штучный: rev. 2026-08-14)
      const marked = (l.children || [])
        .filter(c => c.quantity > 0 && c.marking_code)
        .map(c => ({
          ...lineToPayload(l, 'part'),
          quantity: c.quantity,
          marking_code: c.marking_code!,
        }));
      // rev. 2026-09-16: марок нет (в т.ч. объём 0) — позиция уходит обычной
      // строкой без марки (предварительный документ), а не пропадает молча.
      if (marked.length === 0) return [lineToPayload(l, 'part')];
      return marked;
    }
    return [lineToPayload(l, 'part')];
  });
  const itemsPayload: OrderItemIn[] = [...servicesPayload, ...partsPayload];
  return {
    vehicle_id: draft.vehicle_id,
    // Марка/модель из справочника — backend резолвит в GUID 1С (vehicle_resolver.md)
    vehicle_make: draft.vehicle_make,
    vehicle_model: draft.vehicle_model,
    license_plate: draft.license_plate,
    vin: draft.vin,
    manufacture_year: draft.year,
    color: draft.color,
    client_type: draft.client_type,
    client_id: draft.client_id,
    retail_buyer_name: draft.retail_buyer_name,
    mileage: draft.mileage,
    recommendations: draft.notes || null,
    prepayment_sum: draft.prepayment_sum,
    work_start_date: draft.work_start_date ? draft.work_start_date.toISOString() : null,
    work_end_date: draft.work_end_date ? draft.work_end_date.toISOString() : null,
    items: itemsPayload,
    payment_type: draft.payment_type,
    card_payment: draft.card_payment,
    org_bank_account_id: draft.org_bank_account_id,
  };
};

// Обработчик ошибок API: 422 (Pydantic detail — массив объектов) и прочие строковые detail.
// Паттерн из CLAUDE.md «React crash на 422».
export const formatApiError = (e: any, fallback = 'Не удалось сохранить'): string => {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d.msg).filter(Boolean).join('; ') || fallback;
  }
  if (typeof detail === 'string') return detail;
  return fallback;
};
